import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { CircleHelp, BookOpen, FileDown } from "lucide-react";
import { toast } from "sonner";

const Body = ({ text }) => (
  <div className="space-y-2 text-sm leading-relaxed">
    {text.split(/\n+/).filter(Boolean).map((p, i) => p.startsWith("## ") ? <h4 key={i} className="font-semibold text-primary mt-2">{p.slice(3)}</h4> : p.startsWith("- ") ? <div key={i} className="flex gap-2"><span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-primary shrink-0" />{p.slice(2)}</div> : <p key={i}>{p}</p>)}
  </div>
);

export function HelpButton() {
  const [open, setOpen] = useState(false);
  const [h, setH] = useState(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useEffect(() => { if (open) api.get("/manuals/context", { params: { path: pathname } }).then((r) => setH(r.data)).catch(() => setH(null)); }, [open, pathname]);
  const jump = (i) => api.get(`/manuals/${h.slug}`).then((r) => { const s = r.data.sections[i]; setH({ ...h, section_index: i, heading: s.heading, body: s.body, related: r.data.sections.map((x, n) => ({ index: n, heading: x.heading })).filter((x) => x.index !== i).slice(0, 6) }); });
  const pdf = async () => { try { const r = await api.get(`/manuals/${h.slug}.pdf`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = `RightPay_${h.slug}_manual.pdf`; a.click(); URL.revokeObjectURL(u); } catch { toast.error("Could not build the PDF"); } };
  return (
    <>
      <Button data-testid="context-help-btn" variant="ghost" size="icon" title="Help for this page" onClick={() => setOpen(true)}><CircleHelp className="w-5 h-5" /></Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="context-help-panel">
          <SheetHeader className="text-left">
            <SheetTitle className="font-display flex items-center gap-2"><BookOpen className="w-4 h-4 text-primary" /> {h ? h.heading : "Help"}</SheetTitle>
            <SheetDescription data-testid="context-help-manual">{h ? `From the ${h.manual_title}` : "Loading the guide for this page…"}</SheetDescription>
          </SheetHeader>
          {h && (
            <div className="mt-4 space-y-5">
              <div data-testid="context-help-body"><Body text={h.body} /></div>
              {h.related?.length > 0 && <div><div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Also in this manual</div><ul className="space-y-1">{h.related.map((r) => <li key={r.index}><button data-testid={`context-help-related-${r.index}`} onClick={() => jump(r.index)} className="text-sm text-primary hover:underline text-left">{r.heading}</button></li>)}</ul></div>}
              <div className="flex gap-2 flex-wrap pt-2 border-t border-border">
                <Button size="sm" variant="outline" data-testid="context-help-open-manual" onClick={() => { setOpen(false); navigate("/help"); }}><BookOpen className="w-4 h-4 mr-1.5" /> Open full manual</Button>
                <Button size="sm" data-testid="context-help-pdf" onClick={pdf}><FileDown className="w-4 h-4 mr-1.5" /> Download PDF</Button>
              </div>
            </div>)}
        </SheetContent>
      </Sheet>
    </>
  );
}
